import { Platform } from 'react-native';
import SHA256 from 'sha.js';
import dayjs from 'dayjs';
import { set } from 'lodash-es';
import { RegistrationData, SubmitResponse } from './registrationForm.types';
import { defaultDateFormat, ymdDateFormat } from './registrationForm.constants';
import { MarketingMeta } from '@bonanza-social/toolkit/state';
import { RefinedRegistrationDataSchema } from './registrationForm.schema';

export const getValidationErrors = (data: RegistrationData) => {
  const { error } = RefinedRegistrationDataSchema.safeParse(data);

  const issues = error?.issues || [];

  const mappedErrors = issues.reduce(
    (acc, val) => set(acc, val.path.join('.'), val.message),
    {},
  );

  return mappedErrors;
};

export const mapLimits = (limits: RegistrationData['limits']) =>
  Object.keys(limits).reduce(
    (acc, type) => {
      const limitType = type as keyof typeof limits;

      Object.keys(limits[limitType]).forEach((period) => {
        const limitPeriod = period as keyof (typeof limits)[typeof limitType];
        const amount = limits[limitType][limitPeriod];

        if (amount) {
          acc.push({
            type,
            period,
            amount,
          });
        }
      });

      return acc;
    },
    [] as { type: string; period: string; amount: number }[],
  );

export const mapFormData = (
  data: RegistrationData,
  marketingMeta: MarketingMeta,
  marketingMetaHistory: MarketingMeta[],
  codeChallenge: string,
) => ({
  account: {
    email: data.email,
    password: data.password,
    hasMFAEnabled: false,
    isTestAccount: false,
    personalDetails: {
      firstName: data.firstName ?? null,
      middleName: data.middleName ?? null,
      lastName: data.lastName ?? null,
      gender: data.gender ?? null,
      occupation: data.occupation ?? null,
      industry: data.industry ?? null,
      addressLine1: data.address
        ? `${data.address?.unit} ${data.address?.buildingNumber} ${data.address?.streetName}`
        : null,
      city: data.address?.city ?? null,
      postalCode: data.address?.postalCode ?? null,
      phoneNumber: data.phone?.phoneNumber ?? null,
      phoneCode: data.phone?.phoneCode ?? null,
      birthDate: data.birthDate ?? '0001-01-01',
      province: data.address?.province ?? null,
      provinceCode: data.address?.provinceCode ?? null,
      country: data.address?.country ?? null,
      countryCode: data.address?.countryCode ?? null,
    },
    marketingPreferences: {
      allowEmailMarketingTool: data.marketing,
      allowSMSMarketingTool: data.marketing,
    },
    registrationMeta: {
      gamingLevel: 'Experienced',
      registrationVariant: data.registration_variant ?? null,
      marketingMeta: {
        ...(marketingMeta || {}),
        promotion_id: data.promoId || undefined,
      },
      marketingMetaHistory: marketingMetaHistory || [],
    },
  },
  oAuthParams: {
    clientId: Platform.select({
      ios: 'NA',
      android: 'NA',
      web: 'bonanzasocial-web',
    }),
    scope: 'openid profile bonanzasocial-api offline_access',
    redirectUri: 'bonanzasocial://callback',
    codeChallenge,
  },
  limits: mapLimits(data.limits || {}),
});

export const mapRegistrationCompletedEventData = ({
  formData,
  account,
}: SubmitResponse) => ({
  email: SHA256('sha256').update(formData.email).digest('hex'),
  phoneNumber: SHA256('sha256')
    .update(`${formData.phone.phoneCode}${formData.phone.phoneNumber}`)
    .digest('hex'),
  firstName: SHA256('sha256').update(formData.firstName).digest('hex'),
  lastName: SHA256('sha256').update(formData.lastName).digest('hex'),
  dateOfBirth: SHA256('sha256')
    .update(dayjs(formData.birthDate, defaultDateFormat).format(ymdDateFormat))
    .digest('hex'),
  gender: SHA256('sha256').update(formData.gender.slice(0, 1)).digest('hex'),
  city: SHA256('sha256').update(formData.address.city).digest('hex'),
  state: SHA256('sha256').update(formData.address.province).digest('hex'),
  zipCode: SHA256('sha256').update(formData.address.postalCode).digest('hex'),
  country: SHA256('sha256').update(formData.address.country).digest('hex'),
  accountId: SHA256('sha256').update(`${account.accountId}`).digest('hex'),
  advertiserTrackingEnabled: 'True',
  applicationTrackingEnabled: 'True',
  extinfo: [
    'a2',
    '',
    '',
    '',
    '10.1.1',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
  ],
});
